import React from 'react';
import styled from 'styled-components';

const ProjectsContainer = styled.section`
  background-color: #4B0000;
  background-image: linear-gradient(to bottom, #4B0000, #8B0000);
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: clamp(2.2rem, 5vw, 2.8rem);
  font-weight: 700;
  position: relative;
  padding-bottom: 1.5rem;
  letter-spacing: 1px;
  color: #FFD700;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 5px;
    background: linear-gradient(90deg, #FF4500, #FF8C00);
    border-radius: 3px;
    box-shadow: 0 0 12px rgba(255, 69, 0, 0.5);
  }
`;

const SectionIntro = styled.p`
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 4rem;
  font-size: clamp(1rem, 1.8vw, 1.15rem);
  color: #FFFFE0;
`;

const ProjectCard = styled.div`
  background: rgba(75, 0, 0, 0.9);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: var(--border-radius);
  overflow: hidden;
  border: 1px solid rgba(255, 69, 0, 0.3);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  transition: transform var(--transition-speed) var(--transition-effect),
              box-shadow var(--transition-speed) var(--transition-effect),
              border-color var(--transition-speed) var(--transition-effect);
  display: flex;
  flex-direction: column;
  opacity: 0;
  transform: translateY(30px);
  animation: fadeInUp 0.6s var(--transition-effect) forwards;
  will-change: transform, opacity;
  
  &:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: 0 20px 40px rgba(255, 69, 0, 0.3), 0 10px 30px rgba(0,0,0,0.4);
    border-color: #FF4500;
  }
`;

const ProjectTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #FF4500;
  font-weight: 600;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 2.5rem;
`;

// Remove this duplicate declaration
// const ProjectCard = styled.div`
//   background: rgba(30, 29, 27, 0.9);
//   backdrop-filter: blur(8px);
//   -webkit-backdrop-filter: blur(8px);
//   border-radius: var(--border-radius);
//   overflow: hidden;
//   border: 1px solid rgba(180, 81, 40, 0.3);
//   box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
//   transition: transform var(--transition-speed) var(--transition-effect),
//               box-shadow var(--transition-speed) var(--transition-effect),
//               border-color var(--transition-speed) var(--transition-effect);
//   display: flex;
//   flex-direction: column;
//   opacity: 0;
//   transform: translateY(30px);
//   animation: fadeInUp 0.6s var(--transition-effect) forwards;
//   will-change: transform, opacity;
//   
//   &:hover {
//     transform: translateY(-12px) scale(1.02);
//     box-shadow: 0 20px 40px rgba(180, 81, 40, 0.3), 0 10px 30px rgba(0,0,0,0.4);
//     border-color: #b45128;
//   }
//   
//   &:nth-child(1) { animation-delay: 0.1s; }
//   &:nth-child(2) { animation-delay: 0.2s; }
//   &:nth-child(3) { animation-delay: 0.3s; }
// `;

const ProjectImage = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 10;
  border-bottom: 1px solid var(--border-color);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform var(--transition-speed-slow) var(--transition-effect),
                filter var(--transition-speed-slow) var(--transition-effect);
    will-change: transform;
  }
  
  ${ProjectCard}:hover & img {
    transform: scale(1.15);
    filter: brightness(1.15) contrast(1.1);
  }
`;

const ProjectOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(rgba(70, 22, 8, 0.5), rgba(11, 5, 5, 0.8));
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity var(--transition-speed) var(--transition-effect);
  
  ${ProjectCard}:hover & {
    opacity: 1;
  }
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1.8rem;
`;

const ProjectLink = styled.a`
  color: #f9d776;
  font-size: 2rem;
  background: rgba(180, 81, 40, 0.15);
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: 1px solid rgba(180, 81, 40, 0.3);
  transition: background var(--transition-speed-fast) var(--transition-effect),
              color var(--transition-speed-fast) var(--transition-effect),
              transform var(--transition-speed-fast) var(--transition-effect),
              box-shadow var(--transition-speed-fast) var(--transition-effect);
              
  &:hover {
    background: #b45128;
    color: #f8f083;
    transform: scale(1.15) rotate(10deg);
    box-shadow: 0 0 15px rgba(180, 81, 40, 0.5);
  }
`;

const ProjectContent = styled.div`
  padding: 1.8rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  
  h3 {
    margin-bottom: 0.8rem;
    color: var(--accent-color);
    font-size: clamp(1.4rem, 2.5vw, 1.5rem);
  }
`;

const ProjectTags = styled.div`
  margin-top: auto;
  padding-top: 1.2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const ProjectTag = styled.span`
  background-color: rgba(0, 255, 204, 0.1);
  color: var(--accent-hover);
  font-size: 0.85rem;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-weight: 500;
  border: 1px solid rgba(0, 255, 204, 0.2);
  transition: background-color var(--transition-speed-fast) var(--transition-effect),
              color var(--transition-speed-fast) var(--transition-effect);
              
  &:hover {
    background-color: rgba(0, 255, 204, 0.2);
    color: var(--accent-color);
  }
`;

const Projects = () => {
  const projectsData = [
    {
      id: 1,
      title: "E-Commerce Platform",
      description: "A full-featured online store with product catalog, shopping cart, and secure checkout.",
      image: "https://via.placeholder.com/600x400",
      tags: ["React", "Node.js", "MongoDB", "Stripe"],
      liveLink: "https://example.com",
      codeLink: "https://github.com"
    },
    {
      id: 2,
      title: "Portfolio Website",
      description: "A responsive portfolio website showcasing projects and skills with modern design.",
      image: "https://via.placeholder.com/600x400",
      tags: ["HTML", "CSS", "JavaScript", "GSAP"],
      liveLink: "https://example.com",
      codeLink: "https://github.com"
    },
    {
      id: 3,
      title: "Task Management App",
      description: "A productivity app for managing tasks, projects, and deadlines with team collaboration.",
      image: "https://via.placeholder.com/600x400",
      tags: ["React", "Firebase", "Material UI"],
      liveLink: "https://example.com",
      codeLink: "https://github.com"
    }
  ];

  return (
    <ProjectsContainer id="projects">
      <SectionTitle>My Projects</SectionTitle>
      <SectionIntro>
        Here are some of my recent projects that showcase my skills and experience.
        Each project reflects my passion for creating intuitive and impactful digital experiences.
      </SectionIntro>
      
      <ProjectsGrid>
        {projectsData.map(project => (
          <ProjectCard key={project.id}>
            <ProjectImage>
              <img src={project.image} alt={project.title} />
              <ProjectOverlay>
                <ProjectLinks>
                  <ProjectLink href={project.liveLink} target="_blank" rel="noopener noreferrer" title="View Live">
                    <i className="fas fa-external-link-alt"></i>
                  </ProjectLink>
                  <ProjectLink href={project.codeLink} target="_blank" rel="noopener noreferrer" title="View Code">
                    <i className="fab fa-github"></i>
                  </ProjectLink>
                </ProjectLinks>
              </ProjectOverlay>
            </ProjectImage>
            
            <ProjectContent>
              <h3>{project.title}</h3>
              <p>{project.description}</p>
              
              <ProjectTags>
                {project.tags.map((tag, index) => (
                  <ProjectTag key={index}>{tag}</ProjectTag>
                ))}
              </ProjectTags>
            </ProjectContent>
          </ProjectCard>
        ))}
      </ProjectsGrid>
    </ProjectsContainer>
  );
};

export default Projects;